/**
 * Freezo Chat Widget
 * Premium hemp THCA brand · sales@freezobrand.com · (440) 510-7967
 *
 * Backend: n8n workflow `Freezo Chatbot` → POST {sessionId, chatInput}
 * Memory: persisted per visitor via sessionId in n8n's Simple Memory node
 */
(function () {
  'use strict';

  // ============ CONFIG ============
  const WEBHOOK_URL = 'https://altcan.app.n8n.cloud/webhook/freezo-chatbot';
  const STORAGE_KEY = 'freezo_chat_session';
  const HISTORY_KEY = 'freezo_chat_history';
  const WELCOME_MESSAGE = "Hey, I'm the Freezo assistant. Ask me about strains, COAs, shipping, or anything else. (You must be 21+ to discuss our products.)";

  // ============ STYLES ============
  const css = `
    #freezo-chat-widget, #freezo-chat-widget * {
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    }

    #freezo-chat-bubble {
      position: fixed;
      bottom: 24px;
      right: 24px;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: #FF6900;
      color: #ffffff;
      border: none;
      cursor: pointer;
      box-shadow: 0 4px 16px rgba(255, 105, 0, 0.32), 0 2px 4px rgba(0, 0, 0, 0.08);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 999998;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    #freezo-chat-bubble:hover {
      transform: scale(1.06);
      box-shadow: 0 6px 20px rgba(255, 105, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.08);
    }
    #freezo-chat-bubble:focus-visible {
      outline: 3px solid #1A1A2E;
      outline-offset: 3px;
    }
    #freezo-chat-bubble svg { width: 28px; height: 28px; }

    #freezo-chat-window {
      position: fixed;
      bottom: 100px;
      right: 24px;
      width: 380px;
      max-width: calc(100vw - 32px);
      height: 600px;
      max-height: calc(100vh - 140px);
      background: #ffffff;
      border-radius: 18px;
      box-shadow: 0 12px 48px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.08);
      display: none;
      flex-direction: column;
      overflow: hidden;
      z-index: 999999;
      opacity: 0;
      transform: translateY(12px);
      transition: opacity 0.2s ease, transform 0.2s ease;
    }
    #freezo-chat-window.is-open {
      display: flex;
      opacity: 1;
      transform: translateY(0);
    }

    #freezo-chat-header {
      background: #FF6900;
      color: #ffffff;
      padding: 18px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-shrink: 0;
    }
    #freezo-chat-header-title { display: flex; align-items: center; gap: 12px; }
    #freezo-chat-header-logo {
      width: 32px;
      height: 32px;
      background: #ffffff;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    #freezo-chat-header-logo svg { width: 18px; height: 18px; color: #FF6900; }
    #freezo-chat-header h3 { margin: 0; font-size: 16px; font-weight: 600; letter-spacing: -0.01em; }
    #freezo-chat-header-subtitle { margin: 0; font-size: 12px; opacity: 0.85; font-weight: 400; }
    #freezo-chat-close {
      background: transparent;
      border: none;
      color: #ffffff;
      cursor: pointer;
      padding: 4px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.85;
      transition: opacity 0.15s ease, background 0.15s ease;
    }
    #freezo-chat-close:hover { opacity: 1; background: rgba(255, 255, 255, 0.15); }
    #freezo-chat-close svg { width: 18px; height: 18px; }

    #freezo-chat-messages {
      flex: 1;
      overflow-y: auto;
      padding: 20px 16px;
      background: #FAFAFA;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    #freezo-chat-messages::-webkit-scrollbar { width: 6px; }
    #freezo-chat-messages::-webkit-scrollbar-thumb { background: #E0E0E0; border-radius: 3px; }
    #freezo-chat-messages::-webkit-scrollbar-thumb:hover { background: #BDBDBD; }

    .freezo-msg {
      max-width: 85%;
      padding: 10px 14px;
      border-radius: 14px;
      font-size: 14px;
      line-height: 1.5;
      word-wrap: break-word;
      animation: freezoFadeIn 0.2s ease;
    }
    @keyframes freezoFadeIn {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .freezo-msg-bot {
      align-self: flex-start;
      background: #ffffff;
      color: #1A1A2E;
      border: 1px solid #ECECEC;
      border-bottom-left-radius: 4px;
    }
    .freezo-msg-user {
      align-self: flex-end;
      background: #1A1A2E;
      color: #ffffff;
      border-bottom-right-radius: 4px;
    }
    .freezo-msg p { margin: 0; }
    .freezo-msg p + p { margin-top: 8px; }
    .freezo-msg a { color: #FF6900; text-decoration: underline; }
    .freezo-msg-user a { color: #FFB47A; }
    .freezo-msg ul, .freezo-msg ol { margin: 6px 0; padding-left: 20px; }
    .freezo-msg li { margin: 2px 0; }
    .freezo-msg strong { font-weight: 600; }
    .freezo-msg code {
      background: rgba(0, 0, 0, 0.06);
      padding: 1px 5px;
      border-radius: 4px;
      font-family: ui-monospace, 'SF Mono', Menlo, monospace;
      font-size: 13px;
    }

    .freezo-typing {
      display: flex;
      gap: 4px;
      padding: 12px 14px;
    }
    .freezo-typing span {
      width: 6px;
      height: 6px;
      background: #BDBDBD;
      border-radius: 50%;
      animation: freezoTyping 1.2s infinite ease-in-out;
    }
    .freezo-typing span:nth-child(2) { animation-delay: 0.15s; }
    .freezo-typing span:nth-child(3) { animation-delay: 0.3s; }
    @keyframes freezoTyping {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
      30%           { transform: translateY(-4px); opacity: 1; }
    }

    #freezo-chat-input-form {
      flex-shrink: 0;
      padding: 12px;
      border-top: 1px solid #ECECEC;
      background: #ffffff;
      display: flex;
      gap: 8px;
      align-items: flex-end;
    }
    #freezo-chat-input {
      flex: 1;
      border: 1px solid #E0E0E0;
      border-radius: 10px;
      padding: 10px 14px;
      font-size: 14px;
      color: #1A1A2E;
      resize: none;
      max-height: 100px;
      min-height: 40px;
      line-height: 1.4;
      font-family: inherit;
      outline: none;
      transition: border-color 0.15s ease;
    }
    #freezo-chat-input:focus { border-color: #FF6900; }
    #freezo-chat-input::placeholder { color: #9E9E9E; }
    #freezo-chat-send {
      background: #FF6900;
      color: #ffffff;
      border: none;
      width: 40px;
      height: 40px;
      border-radius: 10px;
      cursor: pointer;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease, transform 0.1s ease;
    }
    #freezo-chat-send:hover:not(:disabled) { background: #E55E00; }
    #freezo-chat-send:active:not(:disabled) { transform: scale(0.95); }
    #freezo-chat-send:disabled { opacity: 0.4; cursor: not-allowed; }
    #freezo-chat-send svg { width: 18px; height: 18px; }

    #freezo-chat-footer {
      flex-shrink: 0;
      padding: 8px 16px 10px;
      background: #ffffff;
      border-top: 1px solid #F5F5F5;
      font-size: 11px;
      color: #999999;
      text-align: center;
      letter-spacing: 0.01em;
    }

    @media (max-width: 480px) {
      #freezo-chat-window {
        bottom: 88px;
        right: 12px;
        left: 12px;
        width: auto;
        height: calc(100vh - 100px);
      }
      #freezo-chat-bubble { bottom: 16px; right: 16px; }
    }
  `;

  // ============ HTML ============
  const widgetHTML = `
    <button id="freezo-chat-bubble" aria-label="Open Freezo chat assistant">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    </button>
    <div id="freezo-chat-window" role="dialog" aria-labelledby="freezo-chat-title">
      <div id="freezo-chat-header">
        <div id="freezo-chat-header-title">
          <div id="freezo-chat-header-logo">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="6,3 18,3 22,9 12,22 2,9"/>
            </svg>
          </div>
          <div>
            <h3 id="freezo-chat-title">Freezo assistant</h3>
            <p id="freezo-chat-header-subtitle">Premium THCa · Lab verified</p>
          </div>
        </div>
        <button id="freezo-chat-close" aria-label="Close chat">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      <div id="freezo-chat-messages" role="log" aria-live="polite"></div>
      <form id="freezo-chat-input-form" autocomplete="off">
        <textarea
          id="freezo-chat-input"
          placeholder="Ask about strains, COAs, shipping..."
          rows="1"
          aria-label="Type your message"
          maxlength="2000"
        ></textarea>
        <button id="freezo-chat-send" type="submit" aria-label="Send message" disabled>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22,2 15,22 11,13 2,9"/>
          </svg>
        </button>
      </form>
      <div id="freezo-chat-footer">21+ only · Federal Farm Bill compliant</div>
    </div>
  `;

  // ============ SESSION + HISTORY ============
  function getSessionId() {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = 'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  }

  function loadHistory() {
    try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; }
  }

  function saveHistory(history) {
    const trimmed = history.slice(-50);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed));
  }

  // ============ MARKDOWN-LITE RENDERER ============
  function renderMarkdown(text) {
    const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    let html = escape(text);
    html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    const lines = html.split('\n');
    let out = [];
    let inList = false;
    for (const line of lines) {
      if (/^[-*]\s+/.test(line)) {
        if (!inList) { out.push('<ul>'); inList = true; }
        out.push('<li>' + line.replace(/^[-*]\s+/, '') + '</li>');
      } else {
        if (inList) { out.push('</ul>'); inList = false; }
        out.push(line);
      }
    }
    if (inList) out.push('</ul>');
    html = out.join('\n');
    html = html.split(/\n{2,}/).map(block => {
      if (block.includes('<ul>')) return block;
      if (block.trim() === '') return '';
      return '<p>' + block.replace(/\n/g, '<br>') + '</p>';
    }).join('');
    return html;
  }

  // ============ UI ============
  let messagesEl, inputEl, sendBtn, windowEl, bubbleEl, formEl, closeBtn;
  let isWaitingForResponse = false;

  function appendMessage(role, text, opts) {
    opts = opts || {};
    const msgEl = document.createElement('div');
    msgEl.className = 'freezo-msg ' + (role === 'user' ? 'freezo-msg-user' : 'freezo-msg-bot');
    msgEl.innerHTML = renderMarkdown(text);
    messagesEl.appendChild(msgEl);
    messagesEl.scrollTop = messagesEl.scrollHeight;
    if (!opts.skipHistory) {
      const history = loadHistory();
      history.push({ role: role, text: text, ts: Date.now() });
      saveHistory(history);
    }
  }

  function appendTyping() {
    const t = document.createElement('div');
    t.className = 'freezo-msg freezo-msg-bot freezo-typing';
    t.id = 'freezo-typing-indicator';
    t.innerHTML = '<span></span><span></span><span></span>';
    messagesEl.appendChild(t);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }
  function removeTyping() {
    const t = document.getElementById('freezo-typing-indicator');
    if (t) t.remove();
  }

  async function sendMessage(text) {
    if (isWaitingForResponse) return;
    isWaitingForResponse = true;
    sendBtn.disabled = true;
    inputEl.disabled = true;

    appendMessage('user', text);
    inputEl.value = '';
    autoResizeInput();
    appendTyping();

    try {
      const res = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: getSessionId(),
          chatInput: text,
          source: 'freezobrand.com',
          page: window.location.pathname,
        }),
      });
      removeTyping();
      if (!res.ok) {
        appendMessage('bot', "Something went sideways on my end. Try again in a moment, or email sales@freezobrand.com.");
        return;
      }
      const data = await res.json();
      const reply = data.output || data.text || data.message || data.response || "I didn't catch that. Try again?";
      appendMessage('bot', reply);
    } catch (err) {
      removeTyping();
      appendMessage('bot', "Connection issue. Try again, or reach the team at sales@freezobrand.com.");
      console.error('[Freezo chat]', err);
    } finally {
      isWaitingForResponse = false;
      sendBtn.disabled = inputEl.value.trim().length === 0;
      inputEl.disabled = false;
      inputEl.focus();
    }
  }

  function autoResizeInput() {
    inputEl.style.height = 'auto';
    inputEl.style.height = Math.min(inputEl.scrollHeight, 100) + 'px';
  }

  function openWindow() {
    windowEl.classList.add('is-open');
    setTimeout(function () { inputEl.focus(); }, 100);
    bubbleEl.style.display = 'none';
  }
  function closeWindow() {
    windowEl.classList.remove('is-open');
    bubbleEl.style.display = '';
  }

  // ============ INIT ============
  function init() {
    const styleEl = document.createElement('style');
    styleEl.textContent = css;
    document.head.appendChild(styleEl);

    const root = document.createElement('div');
    root.id = 'freezo-chat-widget';
    root.innerHTML = widgetHTML;
    document.body.appendChild(root);

    bubbleEl   = document.getElementById('freezo-chat-bubble');
    windowEl   = document.getElementById('freezo-chat-window');
    closeBtn   = document.getElementById('freezo-chat-close');
    messagesEl = document.getElementById('freezo-chat-messages');
    inputEl    = document.getElementById('freezo-chat-input');
    sendBtn    = document.getElementById('freezo-chat-send');
    formEl     = document.getElementById('freezo-chat-input-form');

    const history = loadHistory();
    if (history.length === 0) {
      appendMessage('bot', WELCOME_MESSAGE, { skipHistory: true });
      saveHistory([{ role: 'bot', text: WELCOME_MESSAGE, ts: Date.now() }]);
    } else {
      for (let i = 0; i < history.length; i++) {
        appendMessage(history[i].role, history[i].text, { skipHistory: true });
      }
    }

    bubbleEl.addEventListener('click', openWindow);
    closeBtn.addEventListener('click', closeWindow);

    inputEl.addEventListener('input', function () {
      sendBtn.disabled = inputEl.value.trim().length === 0;
      autoResizeInput();
    });

    inputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (!sendBtn.disabled) formEl.requestSubmit();
      }
    });

    formEl.addEventListener('submit', function (e) {
      e.preventDefault();
      const text = inputEl.value.trim();
      if (text) sendMessage(text);
    });

    // Debug helpers — reset memory or open/close from browser console
    window.FreezoChat = {
      reset: function () {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(HISTORY_KEY);
        location.reload();
      },
      open: openWindow,
      close: closeWindow,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();